import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { SafeImage } from "@/components/common/SafeImage";
import { fallbackCollectionCover } from "@/lib/catalog/fallback-images";
import { retailPacksQuery } from "@/lib/retail/queries";
import { tierRangeLabel, tierSavingPercent } from "@/lib/retail/promotions";
import { canonicalUrl } from "@/lib/seo/site";

export const Route = createFileRoute("/retail/")({
  head: () => ({
    meta: [
      { title: "Retail Fragrance Packs & Multi-Buy Offers — CB16 Atelier" },
      {
        name: "description",
        content: "Ready-to-ship retail fragrance packs with multi-buy savings — starter sets, bestsellers and luxury editions from CB16 Atelier.",
      },
      { property: "og:title", content: "Retail Fragrance Packs & Multi-Buy Offers — CB16 Atelier" },
      { property: "og:description", content: "Ready-to-ship retail packs with multi-buy savings from CB16 Atelier." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: canonicalUrl("/retail") },
    ],
    links: [{ rel: "canonical", href: canonicalUrl("/retail") }],
  }),
  loader: ({ context }) => {
    void context.queryClient.ensureQueryData(retailPacksQuery());
  },
  component: RetailLanding,
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="text-2xl" style={{ fontFamily: "'Playfair Display', serif" }}>Retail packs unavailable</h1>
      <p className="mt-3 text-sm text-black/60">Please refresh in a moment.</p>
    </div>
  ),
});

function money(n: number): string {
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
}

function RetailLanding() {
  const { data } = useSuspenseQuery(retailPacksQuery());
  const packs = data.packs;
  const occasions = Array.from(new Set(packs.map((p) => p.occasion).filter(Boolean))) as string[];

  return (
    <div className="bg-white text-black" style={{ fontFamily: "'Inter', sans-serif" }}>
      <section className="border-b border-black/10 bg-[#faf7f2]">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-24">
          <p className="text-[11px] font-medium uppercase tracking-[0.32em] text-[#C6A15B]">Retail packs</p>
          <h1 className="mt-4 max-w-3xl text-4xl leading-tight sm:text-5xl" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>
            Ready-to-ship sets, priced to reward the second bottle
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-black/65">
            Curated retail packs that ship as-is — no configuration needed. Buy more than one and multi-buy pricing
            applies automatically at checkout.
          </p>
          {occasions.length > 0 && (
            <ul className="mt-8 flex flex-wrap gap-2">
              {occasions.map((o) => (
                <li key={o} className="border border-black/15 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-black/60">{o}</li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
        {data.error && <p className="text-sm text-red-700">{data.error}</p>}
        {!data.error && packs.length === 0 && <p className="text-sm text-black/55">No retail packs are published yet.</p>}

        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {packs.map((p) => (
            <Link
              key={p.slug}
              to="/packs/$slug"
              params={{ slug: p.slug }}
              className="group flex flex-col border border-black/10 transition hover:border-black/30"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[#f4f1ec]">
                <SafeImage
                  src={p.thumbnailImage ?? p.heroImage}
                  fallback={fallbackCollectionCover(p.slug)}
                  alt={p.name}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                />
                {p.bestSavingPercent > 0 && (
                  <span className="absolute left-0 top-4 bg-[#C6A15B] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white">
                    Save up to {p.bestSavingPercent}%
                  </span>
                )}
              </div>
              <div className="flex flex-1 flex-col p-5">
                <h2 className="text-xl" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>{p.name}</h2>
                {p.shortDescription && <p className="mt-2 line-clamp-2 text-sm text-black/55">{p.shortDescription}</p>}
                <p className="mt-3 text-sm text-black/60">{p.bottleCount} × {p.bottleSizeMl} ML</p>
                <p className="mt-1 text-lg font-medium">{money(p.basePrice)}</p>
                {p.tiers.length > 0 && (
                  <ul className="mt-3 space-y-1 text-[12px] text-black/55">
                    {p.tiers.slice(0, 3).map((t, i) => (
                      <li key={i} className="flex justify-between">
                        <span>{tierRangeLabel(t)}</span>
                        <span className="font-medium text-[#8a6d3b]">−{tierSavingPercent(t, p.basePrice)}%</span>
                      </li>
                    ))}
                  </ul>
                )}
                <span className="mt-5 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#C6A15B]">Shop pack →</span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}